import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import './Continuouscare.css';
import {
  Target, Bell, FileText, MessageCircle, Plus, X, Check,
  Trash2, Upload
} from 'lucide-react';
import config from '../../../config';

const TABS = ['Milestones', 'Reminders', 'Documents', 'Doctor Support'];

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

function getAuthToken() {
  return localStorage.getItem('token');
}

async function apiRequest(path, options = {}) {
  const token = getAuthToken();
  const res = await fetch(`${config.baseURL}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  if (res.status === 204) return null;
  const json = await res.json();
  return json.data;
}

const api = {
  milestones: {
    list: (userId, status) => apiRequest(`/milestones?user_id=${userId}${status ? `&status=${status}` : ''}`),
    create: (userId, body) => apiRequest('/milestones', { method: 'POST', body: JSON.stringify({ userId, ...body }) }),
    setStatus: (id, userId, status) =>
      apiRequest(`/milestones/${id}/status?user_id=${userId}`, { method: 'PUT', body: JSON.stringify({ status }) }),
    remove: (id, userId) => apiRequest(`/milestones/${id}?user_id=${userId}`, { method: 'DELETE' }),
  },
  reminders: {
    list: userId => apiRequest(`/reminders?user_id=${userId}`),
    create: (userId, body) => apiRequest('/reminders', { method: 'POST', body: JSON.stringify({ userId, ...body }) }),
    markDone: (id, userId) => apiRequest(`/reminders/${id}/done?user_id=${userId}`, { method: 'PUT' }),
    remove: (id, userId) => apiRequest(`/reminders/${id}?user_id=${userId}`, { method: 'DELETE' }),
  },
  documents: {
    list: userId => apiRequest(`/documents?user_id=${userId}`),
    remove: (id, userId) => apiRequest(`/documents/${id}?user_id=${userId}`, { method: 'DELETE' }),
  },
  support: {
    doctors: userId => apiRequest(`/support/doctors?user_id=${userId}`),
    list: userId => apiRequest(`/support?user_id=${userId}`),
    create: (userId, body) => apiRequest('/support', { method: 'POST', body: JSON.stringify({ userId, ...body }) }),
    // NEW — thread endpoints
    getMessages: (ticketId, userId) => apiRequest(`/support/${ticketId}/messages?user_id=${userId}`),
    addMessage: (ticketId, userId, message) =>
      apiRequest(`/support/${ticketId}/messages`, { method: 'POST', body: JSON.stringify({ userId, message }) }),
  },
};

async function uploadDocument(userId, file, category) {
  const token = getAuthToken();
  const form = new FormData();
  form.append('file', file);
  form.append('userId', userId);
  if (category) form.append('category', category);

  const res = await fetch(`${config.baseURL}/api/documents/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Upload failed (${res.status})`);
  return body.data;
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function formatDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// Milestones tab — no dummy fallback: real data, an empty state, or a
// visible error if the request fails.
// ---------------------------------------------------------------------------

function MilestonesTab({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.milestones.list(userId)
      .then(rows => setItems(rows || []))
      .catch(e => {
        setError(e.message || 'Failed to load milestones');
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd() {
    if (!title) return;
    setSaving(true);
    setError(null);
    try {
      const created = await api.milestones.create(userId, { title, targetDate: targetDate || null });
      setItems(prev => [...prev, created]);
      setTitle(''); setTargetDate(''); setShowForm(false);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function toggle(m) {
    const next = m.status === 'completed' ? 'pending' : 'completed';
    try {
      const updated = await api.milestones.setStatus(m.id, userId, next);
      setItems(prev => prev.map(i => (i.id === m.id ? updated : i)));
    } catch (e) {
      setError(e.message || 'Failed to update milestone');
    }
  }

  async function remove(id) {
    try {
      await api.milestones.remove(id, userId);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      setError(e.message || 'Failed to delete milestone');
    }
  }

  if (loading) return <p className="muted-text">Loading milestones…</p>;

  return (
    <div className="section-stack">
      {error && <p className="error-text">{error}</p>}
      <div className="list-stack">
        {items.length === 0 && <p className="muted-text">No milestones yet.</p>}
        {items.map(m => (
          <div key={m.id} className="row-card">
            <button onClick={() => toggle(m)} className={`check-circle ${m.status === 'completed' ? 'checked' : ''}`}>
              {m.status === 'completed' && <Check size={12} />}
            </button>
            <div className="row-content">
              <p className={`row-title ${m.status === 'completed' ? 'done' : ''}`}>{m.title}</p>
              {m.description && <p className="row-desc">{m.description}</p>}
              {m.target_date && <p className="row-meta">Target: {formatDate(m.target_date)}</p>}
            </div>
            <button onClick={() => remove(m.id)} className="icon-btn"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {showForm ? (
        <div className="inline-form">
          <input className="field-input" placeholder="Milestone title" value={title} onChange={e => setTitle(e.target.value)} />
          <input className="field-input" type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} />
          <div className="inline-form-actions">
            <button onClick={handleAdd} disabled={!title || saving} className="btn-primary">{saving ? 'Saving…' : 'Add'}</button>
            <button onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowForm(true)} className="add-btn"><Plus size={15} /> Add milestone</button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reminders tab
// ---------------------------------------------------------------------------

function RemindersTab({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [remindAt, setRemindAt] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.reminders.list(userId)
      .then(rows => setItems(rows || []))
      .catch(e => {
        setError(e.message || 'Failed to load reminders');
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd() {
    if (!title || !remindAt) return;
    setSaving(true);
    setError(null);
    try {
      const created = await api.reminders.create(userId, { title, remindAt, type: 'custom' });
      setItems(prev => [...prev, created].sort((a, b) => a.remind_at.localeCompare(b.remind_at)));
      setTitle(''); setRemindAt(''); setShowForm(false);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function done(id) {
    try {
      await api.reminders.markDone(id, userId);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      setError(e.message || 'Failed to update reminder');
    }
  }

  async function remove(id) {
    try {
      await api.reminders.remove(id, userId);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      setError(e.message || 'Failed to delete reminder');
    }
  }

  if (loading) return <p className="muted-text">Loading reminders…</p>;

  return (
    <div className="section-stack">
      {error && <p className="error-text">{error}</p>}
      <div className="list-stack">
        {items.length === 0 && <p className="muted-text">No upcoming reminders.</p>}
        {items.map(r => (
          <div key={r.id} className="row-card">
            <span className="row-icon"><Bell size={15} /></span>
            <div className="row-content">
              <p className="row-title">{r.title}</p>
              <p className="row-meta">{formatDateTime(r.remind_at)}</p>
            </div>
            <button onClick={() => done(r.id)} className="icon-btn"><Check size={14} /></button>
            <button onClick={() => remove(r.id)} className="icon-btn"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {showForm ? (
        <div className="inline-form">
          <input className="field-input" placeholder="Reminder title" value={title} onChange={e => setTitle(e.target.value)} />
          <input className="field-input" type="datetime-local" value={remindAt} onChange={e => setRemindAt(e.target.value)} />
          <div className="inline-form-actions">
            <button onClick={handleAdd} disabled={!title || !remindAt || saving} className="btn-primary">{saving ? 'Saving…' : 'Add'}</button>
            <button onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowForm(true)} className="add-btn"><Plus size={15} /> Add reminder</button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documents tab
// ---------------------------------------------------------------------------

function DocumentsTab({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.documents.list(userId)
      .then(rows => setItems(rows || []))
      .catch(e => {
        setError(e.message || 'Failed to load documents');
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  async function handleFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      const created = await uploadDocument(userId, file, 'General');
      setItems(prev => [created, ...prev]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function remove(id) {
    try {
      await api.documents.remove(id, userId);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      setError(e.message || 'Failed to delete document');
    }
  }

  if (loading) return <p className="muted-text">Loading documents…</p>;

  return (
    <div className="section-stack">
      {error && <p className="error-text">{error}</p>}
      <div className="list-stack">
        {items.length === 0 && <p className="muted-text">No documents uploaded yet.</p>}
        {items.map(d => (
          <div key={d.id} className="row-card">
            <span className="row-icon"><FileText size={15} /></span>
            <div className="row-content">
              <p className="row-title">{d.name}</p>
              <p className="row-meta">{d.category} · {formatDate(d.uploaded_at)}{d.size_kb ? ` · ${d.size_kb} KB` : ''}</p>
            </div>
            <button onClick={() => remove(d.id)} className="icon-btn"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      <label className="add-btn" style={{ cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
        <Upload size={15} /> {uploading ? 'Uploading…' : 'Upload a document'}
        <input type="file" onChange={handleFilePicked} disabled={uploading} style={{ display: 'none' }} />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Support tab — chat thread + popup modal
// ---------------------------------------------------------------------------

function ChatBubble({ msg, isMine }) {
  return (
    <div style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
      <div
        style={{
          maxWidth: '75%',
          padding: '10px 14px',
          borderRadius: 14,
          borderBottomRightRadius: isMine ? 4 : 14,
          borderBottomLeftRadius: isMine ? 14 : 4,
          background: isMine ? '#0F4C75' : '#F2F3F4',
          color: isMine ? 'white' : '#222',
        }}
      >
        <p style={{ margin: 0, fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{msg.message}</p>
        <p style={{ margin: '4px 0 0', fontSize: 10.5, opacity: 0.7, textAlign: 'right' }}>
          {formatDateTime(msg.created_at)}
        </p>
      </div>
    </div>
  );
}

// Clicking a ticket in the list opens this — the whole thread, with a send
// box at the bottom.
function TicketChatModal({ ticketId, userId, onClose, onStatusMayHaveChanged }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.support.getMessages(ticketId, userId)
      .then(res => {
        setTicket(res.ticket);
        setMessages(res.messages);
      })
      .catch(e => setError(e.message || 'Failed to load conversation'))
      .finally(() => setLoading(false));
  }, [ticketId, userId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  async function handleSend() {
    if (!draft.trim()) return;
    setSending(true);
    setError(null);
    try {
      const res = await api.support.addMessage(ticketId, userId, draft);
      setTicket(res.ticket);
      setMessages(res.messages);
      setDraft('');
      onStatusMayHaveChanged(); // sending a message may have reopened the ticket — let the list know to refresh
    } catch (err) {
      setError(err.message || 'Could not send message');
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white', borderRadius: 14, width: '100%', maxWidth: 520,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #EEF0F2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{ticket?.subject || 'Loading…'}</p>
            {ticket && (
              <p style={{ margin: '2px 0 0', fontSize: 11.5, color: '#888780' }}>
                Ticket #{ticket.id} · <span className={`status-pill status-${ticket.status}`} style={{ fontSize: 10.5, padding: '2px 8px' }}>{ticket.status}</span>
              </p>
            )}
          </div>
          <button onClick={onClose} className="icon-btn" aria-label="Close"><X size={16} /></button>
        </div>

        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, minHeight: 260 }}>
          {loading ? (
            <p className="muted-text" style={{ textAlign: 'center' }}>Loading…</p>
          ) : messages.length === 0 ? (
            <p className="muted-text" style={{ textAlign: 'center' }}>No messages yet.</p>
          ) : (
            messages.map(m => <ChatBubble key={m.id} msg={m} isMine={m.sender_type === 'patient'} />)
          )}
        </div>

        {error && <p className="error-text" style={{ padding: '0 16px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #EEF0F2' }}>
          <textarea
            className="field-input"
            rows={1}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Type a message…"
            disabled={sending}
            style={{ flex: 1, resize: 'none' }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button onClick={handleSend} disabled={!draft.trim() || sending} className="btn-primary">
            <MessageCircle size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SupportTab({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [openTicketId, setOpenTicketId] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.support.list(userId)
      .then(rows => setItems(rows || []))
      .catch(e => {
        setError(e.message || 'Failed to load doctor support messages');
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Only doctors this patient has booked can be contacted.
  useEffect(() => {
    api.support.doctors(userId)
      .then(rows => {
        setDoctors(rows || []);
        if (rows && rows.length === 1) setDoctorId(String(rows[0].id));
      })
      .catch(e => setError(e.message || 'Failed to load your doctors'));
  }, [userId]);

  async function handleSend() {
    if (!doctorId || !subject || !message) return;
    setSaving(true);
    setError(null);
    try {
      const created = await api.support.create(userId, { doctorId: Number(doctorId), subject, message });
      setItems(prev => [created, ...prev]);
      setSubject(''); setMessage(''); setDoctorId(doctors.length === 1 ? String(doctors[0].id) : ''); setShowForm(false);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  if (loading) return <p className="muted-text">Loading doctor support messages…</p>;

  return (
    <div className="section-stack">
      {error && <p className="error-text">{error}</p>}
      <div className="list-stack">
        {items.length === 0 && <p className="muted-text">No messages yet — reach out below if you need help.</p>}
        {items.map(m => (
          <button
            key={m.id}
            onClick={() => setOpenTicketId(m.id)}
            className="row-card"
            style={{ alignItems: 'flex-start', width: '100%', textAlign: 'left', cursor: 'pointer', border: 'none', background: 'white' }}
          >
            <span className="row-icon"><MessageCircle size={15} /></span>
            <div className="row-content">
              <p className="row-title">{m.subject} <span style={{ fontWeight: 400, color: '#888780', fontSize: 11.5 }}>#{m.id}</span></p>
              {m.doctor_name && <p className="row-meta">To {m.doctor_name}</p>}
              {m.last_message && <p className="row-desc">{m.last_message}</p>}
              <p className="row-meta">{formatDateTime(m.last_message_at || m.created_at)}</p>
            </div>
            <span className={`status-pill status-${m.status}`}>{m.status}</span>
          </button>
        ))}
      </div>

      {showForm ? (
        <div className="inline-form">
          <select className="field-input" value={doctorId} onChange={e => setDoctorId(e.target.value)}>
            <option value="">Select a doctor…</option>
            {doctors.map(d => (
              <option key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ''}</option>
            ))}
          </select>
          <input className="field-input" placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} />
          <textarea className="field-input" rows={3} placeholder="How can we help?" value={message} onChange={e => setMessage(e.target.value)} />
          <div className="inline-form-actions">
            <button onClick={handleSend} disabled={!doctorId || !subject || !message || saving} className="btn-primary">{saving ? 'Sending…' : 'Send'}</button>
            <button onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      ) : (
        doctors.length === 0 ? (
          <p className="muted-text">Book an appointment first — you can then message that doctor here.</p>
        ) : (
          <button onClick={() => setShowForm(true)} className="add-btn"><Plus size={15} /> Contact Doctor support</button>
        )
      )}

      {openTicketId && (
        <TicketChatModal
          ticketId={openTicketId}
          userId={userId}
          onClose={() => setOpenTicketId(null)}
          onStatusMayHaveChanged={load}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ContinuousCare = () => {
  const { userInfo } = useSelector((state) => state.user);
  const userId = userInfo?.id;

  const [tab, setTab] = useState('Milestones');
  const [hasBooking, setHasBooking] = useState(false);

  // The Doctor Support tab only appears once the patient has booked at least one appointment.
  useEffect(() => {
    if (!userId) return;
    api.support.doctors(userId)
      .then(rows => setHasBooking((rows || []).length > 0))
      .catch(() => setHasBooking(false));
  }, [userId]);

  const visibleTabs = hasBooking ? TABS : TABS.filter(t => t !== 'Doctor Support');

  if (!userId) {
    return (
      <div className="cc-page">
        <div className="cc-container">
          <p className="muted-text">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-page">
      <div className="cc-header">
        <div className="cc-header-inner">
          <h1 className="cc-title">Continuous care</h1>
          <p className="cc-subtitle">Milestones, reminders, documents, and doctor support in one place</p>
        </div>
      </div>

      <div className="cc-container">
        <div className="health-tabs">
          {visibleTabs.map(t => {
            const Icon = { Milestones: Target, Reminders: Bell, Documents: FileText, 'Doctor Support': MessageCircle }[t];
            return (
              <button key={t} onClick={() => setTab(t)} className={`health-tab ${tab === t ? 'active' : ''}`}>
                <Icon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                {t}
              </button>
            );
          })}
        </div>

        <div className="tab-content">
          {tab === 'Milestones' && <MilestonesTab userId={userId} />}
          {tab === 'Reminders' && <RemindersTab userId={userId} />}
          {tab === 'Documents' && <DocumentsTab userId={userId} />}
          {tab === 'Doctor Support' && hasBooking && <SupportTab userId={userId} />}
        </div>
      </div>
    </div>
  );
};

export default ContinuousCare;